import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SchoolSummaryDto } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorState, SkeletonRows } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatNumber } from '../lib/format';

/**
 * A07 — look inside one school's records, for support.
 *
 * A fixed set of tables against one school at a time, never a query box. The
 * questions support actually gets ("is this parent linked to that child",
 * "did that payment land") are answered by these eight views; a free-text SQL
 * console would answer them too, and also answer questions nobody should be
 * able to ask by accident.
 */

const TABLES = [
  ['students', 'Students'],
  ['guardians', 'Parents'],
  ['staff', 'Staff'],
  ['accounts', 'Logins'],
  ['invoices', 'Invoices'],
  ['payments', 'Payments'],
  ['attendance', 'Attendance'],
  ['notices', 'Notices'],
] as const;

type Table = (typeof TABLES)[number][0];

interface BrowseResponse {
  school: { id: string; name: string };
  table: Table;
  columns: string[];
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  pageSize: number;
}

export function DataExplorerPage() {
  const [schoolId, setSchoolId] = useState('');
  const [table, setTable] = useState<Table>('students');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const schools = useQuery({
    queryKey: ['schools'],
    queryFn: () => api.get<{ items: SchoolSummaryDto[] }>('/api/schools'),
  });

  const browse = useQuery({
    queryKey: ['data-explorer', schoolId, table, applied, page],
    enabled: schoolId !== '',
    queryFn: () =>
      api.get<BrowseResponse>(
        `/api/data-explorer?schoolId=${schoolId}&table=${table}&page=${page}` +
          (applied ? `&q=${encodeURIComponent(applied)}` : ''),
      ),
  });

  const pageCount = browse.data ? Math.max(1, Math.ceil(browse.data.total / browse.data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Data explorer"
        meta="Read-only. Every look is recorded in the audit log."
      />

      <div className="page">
        <div className="card">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: '1 1 260px' }}>
              <span>School</span>
              <select
                value={schoolId}
                onChange={(e) => {
                  setSchoolId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Choose a school…</option>
                {(schools.data?.items ?? []).map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field" style={{ flex: '2 1 320px' }}>
              <span>Search</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setApplied(search.trim());
                    setPage(1);
                  }
                }}
                placeholder="Name, admission number, phone, invoice number…"
              />
            </label>

            <button
              className="btn"
              onClick={() => {
                setApplied(search.trim());
                setPage(1);
              }}
              disabled={!schoolId}
            >
              Search
            </button>
          </div>

          <div className="segmented" style={{ marginTop: 16, flexWrap: 'wrap' }}>
            {TABLES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={table === value}
                onClick={() => {
                  setTable(value);
                  setPage(1);
                }}
                style={{ padding: '10px 14px', flex: 'none' }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!schoolId ? (
          <EmptyState
            title="Pick a school"
            message="Every view here is scoped to one school at a time — choose one above to look at its records."
          />
        ) : browse.error ? (
          <ErrorState
            message={browse.error instanceof ApiRequestError ? browse.error.message : 'Could not load that view'}
            onRetry={() => void browse.refetch()}
          />
        ) : browse.isPending ? (
          <SkeletonRows count={6} />
        ) : browse.data.rows.length === 0 ? (
          <EmptyState
            title="Nothing matches"
            message={applied ? `No rows for "${applied}" in this view.` : 'This school has no rows in this table.'}
          />
        ) : (
          <>
            <div className="card card--flush">
              <table className="table">
                <thead>
                  <tr>
                    {browse.data.columns.map((column) => (
                      <th key={column}>{humanise(column)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {browse.data.rows.map((row, index) => (
                    <tr key={index}>
                      {browse.data!.columns.map((column) => (
                        <td key={column} style={{ fontSize: 13 }}>
                          {String(row[column] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="caption">
                {formatNumber(browse.data.total)} rows · page {browse.data.page} of {pageCount}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** "admissionNumber" → "Admission number" */
function humanise(column: string): string {
  const spaced = column.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
